import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@dawn/ui';
import { ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { SESSION_CHANNEL_NAME } from '@/providers/SessionProvider';
import { useSessionStore } from '@/stores/session';

function getExpiryMessage(reason: string | null): string {
  switch (reason) {
    case 'inactivity':
      return 'Your session has expired due to inactivity. Please log in again to continue.';
    case 'max_age':
      return 'Your session has reached its maximum duration. Please log in again.';
    case 'revoked':
    case 'invalid':
    default:
      return 'Your session is no longer valid. Please log in again.';
  }
}

export function SessionExpiredModal() {
  const navigate = useNavigate();
  const { status, expiryReason, setUnauthenticated } = useSessionStore();

  const handleLogin = () => {
    try {
      // pathname + search: a query-carrying page (MCP OAuth consent) must survive re-login.
      sessionStorage.setItem('returnUrl', window.location.pathname + window.location.search);
    } catch {
      // Storage unavailable — proceed without returnUrl
    }
    const ch = new BroadcastChannel(SESSION_CHANNEL_NAME);
    ch.postMessage({ type: 'forced_redirect' });
    ch.close();
    setUnauthenticated();
    navigate('/login');
  };

  return (
    <Dialog open={status === 'expired'}>
      <DialogContent
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="bg-white dark:bg-zinc-900 shadow-2xl border-red-200 dark:border-red-800"
      >
        <DialogHeader className="items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <ShieldAlert
              className="h-6 w-6 text-red-600 dark:text-red-400"
              data-testid="session-expired-icon"
            />
          </div>
          <DialogTitle className="text-center">Session Expired</DialogTitle>
          <DialogDescription className="text-center">
            {getExpiryMessage(expiryReason)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button
            variant="destructive"
            onClick={handleLogin}
            className="cursor-pointer"
            data-testid="session-expired-login-button"
          >
            Log In
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
